import React from 'react';
import { motion } from 'framer-motion';
import { Handshake, CheckCircle } from 'lucide-react';

export const Partner = () => {
  const partnerPoints = [
    'Strengthen environmental education and youth engagement',
    'Communicate funded initiatives clearly and responsibly',
    'Build long term impact through storytelling and learning',
  ];

  // Partner logos - all 8 partners
  const partnerLogos = [
    { name: 'Hunt Global', url: 'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/ucwx03j5_openart-image_1774868290463_b4f8f987_1774868290481_461d17a8%20-%20Edited.png' },
    { name: 'Alentejo Caiado de Fresco', url: 'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/et7k0o3x_openart-image_1774868523864_b314cf17_1774868523893_107b9023%20-%20Edited.png' },
    { name: 'Azores', url: 'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/n7gq2983_openart-image_1774869471229_4e4bb757_1774869471278_4fc98b70%20-%20Edited.png' },
    { name: 'Alentejo Ribatejo', url: 'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/cww73zng_openart-image_1774871852805_658f6e09_1774871853029_65d46d96%20-%20Edited.png' },
    { name: 'RTP', url: 'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/8q58snvs_openart-image_1774872428196_34853b73_1774872428407_92cc2476%20-%20Edited.png' },
    { name: 'Visit Angola', url: 'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/7ns8cndn_openart-image_1774872926829_672bdb9a_1774872927212_4a0fd958%20-%20Edited.png' },
    { name: 'Orada', url: 'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/fybekz74_openart-image_1774873501287_0cb01a55_1774873501352_71b0b783%20-%20Edited.png' },
    { name: 'MOVE Monte Velho', url: 'https://customer-assets.emergentagent.com/job_f6a60205-fe70-4887-9d6d-271a2734bf52/artifacts/mw5tl3vs_openart-image_1774873764111_52ffeaab_1774873764144_94122955%20-%20Edited.png' },
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="partner"
      className="py-20 md:py-32 px-4 md:px-6 lg:px-8 bg-[#1A2D23] text-[#FDFCF8] relative overflow-hidden"
      data-testid="partner-section"
    >
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_site-creator-909/artifacts/9ycb4uy7_Conservation-and-restoration-min.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      <div className="container mx-auto max-w-5xl text-center relative z-10">
        {/* Partner Logos Showcase - Integrated with Icon */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          <div className="inline-block p-5 bg-[#2E5C44] rounded-full mb-2">
            <Handshake className="w-16 h-16 text-white" />
          </div>
          
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">
            Partner With Us
          </h2>
          
          <p className="text-sm uppercase tracking-widest text-[#E8E6DE]/70 pt-4">
            Trusted by leading organizations
          </p>
          
          {/* Logo Grid - 2 rows, 4 per row */}
          <div className="relative py-8">
            {/* Subtle gradient overlay for readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/20 rounded-3xl" />
            
            <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5 md:gap-x-12 md:gap-y-6 lg:gap-x-16 lg:gap-y-8 px-4 md:px-8 py-6 max-w-4xl mx-auto">
              {partnerLogos.map((logo, index) => (
                <motion.div
                  key={logo.name}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center justify-center p-2"
                >
                  <img
                    src={logo.url}
                    alt={logo.name}
                    className="h-20 md:h-24 lg:h-28 w-auto max-w-[140px] md:max-w-[160px] object-contain opacity-95 hover:opacity-100 transition-all duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </div>

          <p className="text-lg md:text-xl leading-relaxed text-[#E8E6DE] max-w-3xl mx-auto pt-4">
            Green Lens Collective supports institutions and organizations seeking
            to:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto my-12">
            {partnerPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-start gap-3 text-left"
                data-testid={`partner-point-${index}`}
              >
                <CheckCircle className="w-6 h-6 text-[#D97706] flex-shrink-0 mt-1" />
                <span className="text-base leading-relaxed">{point}</span>
              </motion.div>
            ))}
          </div>

          <p className="text-lg leading-relaxed text-[#E8E6DE] italic">
            We are an independent partner committed to clarity, integrity, and
            environmental action.
          </p>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            onClick={scrollToContact}
            className="rounded-full bg-[#D97706] text-white hover:bg-[#B45309] px-12 py-6 text-xl font-medium shadow-2xl hover:shadow-3xl transition-all duration-300 mt-8"
            data-testid="partner-cta-btn"
          >
            Start a Conversation
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Partner;